import { useState, useEffect, useRef } from "react";
import { ArrowCounterClockwise } from "@phosphor-icons/react";

const PLACEHOLDER_STEPS = [
  {
    title: "Prepare your ingredients",
    body: "Gather all your ingredients and get everything measured and ready to go. Having everything prepped before you start makes the whole process much smoother.",
    time: null,
  },
  {
    title: "Heat the pan",
    body: "Place a large pan or skillet over medium-high heat. Add a drizzle of oil and let it heat until it shimmers. You want the pan hot before adding anything.",
    time: 2,
  },
  {
    title: "Cook the main ingredient",
    body: "Add your main ingredient to the pan. Don't move it too much — let it develop colour and flavour. Cook, turning occasionally, until golden brown and cooked through.",
    time: 8,
  },
  {
    title: "Season and finish",
    body: "Taste and season with salt and pepper. Add any finishing touches — a squeeze of lemon, fresh herbs, or a drizzle of olive oil. Plate up and serve immediately.",
    time: null,
  },
];

const PLACEHOLDER_TIPS = [
  "Prep everything before you start cooking for a smoother experience.",
  "A hot pan means better colour and flavour — don't rush this step.",
  "Resist the urge to move things around too much. Patience = flavour.",
  "Taste as you go. Your palate is the best tool in the kitchen.",
];

const PLACEHOLDER_INGREDIENTS = [
  "2 chicken breasts",
  "2 tbsp olive oil",
  "3 cloves garlic, minced",
  "1 tsp paprika",
  "Salt and black pepper",
  "Fresh parsley to serve",
];

const COMPLETION_MESSAGES = [
  "That's what we're talking about.",
  "Nailed it.",
  "Another one. 🔥",
  "Look at you go.",
  "Honestly? Impressive.",
  "That smells incredible.",
  "You just fed yourself well. That matters.",
  "One meal closer to the best version of you.",
  "Clean plate energy.",
  "Did you just cook that? Yes you did.",
];

function CookingMode({ recipe, isMobile, appTheme, onClose, onMarkCooked, existingNote, onSaveNote }) {
  const [screen, setScreen] = useState("ingredients");
  const [checkedIngredients, setCheckedIngredients] = useState(new Set());
  const [currentStep, setCurrentStep] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState(null);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerDone, setTimerDone] = useState(false);
  const [isCooked, setIsCooked] = useState(false);
  const [noteDraft, setNoteDraft] = useState(existingNote || "");
  const [noteSaved, setNoteSaved] = useState(false);

  const completionMessage = useRef(
    COMPLETION_MESSAGES[Math.floor(Math.random() * COMPLETION_MESSAGES.length)]
  );
  const timerRef = useRef(null);

  const ingredients = Array.isArray(recipe?.ingredients) && recipe.ingredients.length > 0
    ? recipe.ingredients
    : PLACEHOLDER_INGREDIENTS;

  const steps = PLACEHOLDER_STEPS;
  const totalSteps = steps.length;
  const currentStepData = steps[currentStep];
  const currentTip = PLACEHOLDER_TIPS[currentStep % PLACEHOLDER_TIPS.length];
  const isLastStep = currentStep === totalSteps - 1;

  const ACCENT = appTheme.accentColor;

  // Timer tick
  useEffect(() => {
    if (timerRunning && timerSeconds > 0) {
      timerRef.current = setInterval(() => {
        setTimerSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setTimerRunning(false);
            setTimerDone(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerRunning]);

  // Reset timer on step change
  useEffect(() => {
    clearInterval(timerRef.current);
    setTimerSeconds(null);
    setTimerRunning(false);
    setTimerDone(false);
  }, [currentStep]);

  function startTimer(minutes) {
    clearInterval(timerRef.current);
    setTimerSeconds(minutes * 60);
    setTimerRunning(true);
    setTimerDone(false);
  }

  function resetTimer() {
    clearInterval(timerRef.current);
    setTimerSeconds(null);
    setTimerRunning(false);
    setTimerDone(false);
  }

  function handleTimerPress(minutes) {
    if (timerSeconds === null || timerDone) {
      startTimer(minutes);
    } else if (timerRunning) {
      clearInterval(timerRef.current);
      setTimerRunning(false);
    } else {
      setTimerRunning(true);
    }
  }

  function formatTimer(s) {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  }

  const pad = isMobile ? "20px" : "28px";

  // ── Screen 1: Ingredients ──────────────────────────────────────────────────
  if (screen === "ingredients") {
    const total = ingredients.length;
    const checked = checkedIngredients.size;
    return (
      <div style={{
        position: "fixed", inset: 0, zIndex: 2500,
        background: appTheme.pageBackground,
        display: "flex", flexDirection: "column",
        animation: "settingsSheetIn 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
      }}>
        {/* Top bar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", padding: `14px ${pad} 0`, flexShrink: 0 }}>
          <button
            onClick={onClose}
            style={{ width: "34px", height: "34px", borderRadius: "999px", border: appTheme.pillBorder, background: appTheme.pillBackground, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1l10 10M11 1L1 11" stroke={appTheme.secondaryText} strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Header */}
        <div style={{ padding: `16px ${pad} 20px`, flexShrink: 0 }}>
          {/* Image + title row */}
          <div style={{ display: "flex", gap: "16px", alignItems: "flex-start", marginBottom: "14px" }}>
            <div style={{
              width: isMobile ? "120px" : "140px",
              height: isMobile ? "120px" : "140px",
              borderRadius: "22px",
              flexShrink: 0,
              background: recipe?.image
                ? `url(${recipe.image}) center/cover no-repeat`
                : "linear-gradient(135deg, #d9f4ef 0%, #e9f3ff 100%)",
              border: appTheme.cardBorder,
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "14px", fontWeight: "600", color: appTheme.secondaryText, marginBottom: "8px", letterSpacing: "0.01em" }}>
                {recipe?.name}
              </div>
              <div style={{ fontSize: isMobile ? "28px" : "34px", fontWeight: "600", fontFamily: appTheme.fontHeading, color: appTheme.primaryText, lineHeight: 1.15 }}>
                Let's get started
              </div>
            </div>
          </div>
          {/* Subtitle + music prompt — full width below */}
          <div style={{ fontSize: "14px", color: appTheme.secondaryText, lineHeight: 1.6, marginBottom: "8px" }}>
            Grab everything you need before you begin
          </div>
        </div>

        {/* Ingredient list */}
        <div className="modal-scroll-hidden" style={{ flex: 1, overflowY: "auto", padding: `0 ${pad}` }}>
          <div style={{ fontSize: "13px", fontWeight: "700", color: appTheme.secondaryText, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: "4px" }}>
            Ingredients
          </div>
          {ingredients.map((ingredient, i) => {
            const isChecked = checkedIngredients.has(i);
            return (
              <div
                key={i}
                onClick={() => setCheckedIngredients((prev) => {
                  const next = new Set(prev);
                  if (next.has(i)) next.delete(i); else next.add(i);
                  return next;
                })}
                style={{
                  display: "flex", alignItems: "center", gap: "14px",
                  padding: "13px 0",
                  borderBottom: i < ingredients.length - 1 ? "1px solid rgba(0,0,0,0.05)" : "none",
                  cursor: "pointer",
                }}
              >
                <div style={{
                  width: "24px", height: "24px", borderRadius: "50%", flexShrink: 0,
                  border: isChecked ? `2px solid ${ACCENT}` : "2px solid rgba(0,0,0,0.18)",
                  background: isChecked ? ACCENT : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.18s ease",
                }}>
                  {isChecked && (
                    <svg viewBox="0 0 12 12" width="11" height="11" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 6l3 3 5-5"/>
                    </svg>
                  )}
                </div>
                <span style={{
                  fontSize: "15px", color: isChecked ? appTheme.secondaryText : appTheme.primaryText,
                  textDecoration: isChecked ? "line-through" : "none",
                  opacity: isChecked ? 0.5 : 1,
                  transition: "all 0.18s ease",
                  lineHeight: 1.4,
                }}>
                  {typeof ingredient === "object" && ingredient !== null ? (
                    <>
                      {ingredient.quantity && <span style={{ opacity: 0.6, marginRight: "5px" }}>{ingredient.quantity}</span>}
                      {ingredient.name}
                    </>
                  ) : ingredient}
                </span>
              </div>
            );
          })}
          <div style={{ height: "16px" }} />
        </div>

        {/* Bottom bar */}
        <div style={{ padding: `10px ${pad} ${isMobile ? "28px" : "20px"}`, borderTop: "1px solid rgba(0,0,0,0.06)", background: appTheme.pageBackground, flexShrink: 0 }}>
          <div style={{ fontSize: "12px", color: appTheme.secondaryText, fontWeight: "500", textAlign: "center", marginBottom: "12px" }}>
            {checked} / {total} ingredients ready
          </div>
          <button
            onClick={() => setScreen("steps")}
            style={{ width: "100%", padding: "15px", borderRadius: "16px", border: "none", background: ACCENT, color: "#fff", fontSize: "16px", fontWeight: "700", cursor: "pointer" }}
          >
            Start Cooking →
          </button>
        </div>
      </div>
    );
  }

  // ── Screen 2: Steps ────────────────────────────────────────────────────────
  if (screen === "steps") {
    const progressPct = ((currentStep + 1) / totalSteps) * 100;
    return (
      <div style={{
        position: "fixed", inset: 0, zIndex: 2500,
        background: appTheme.pageBackground,
        display: "flex", flexDirection: "column",
      }}>
        {/* Top bar */}
        <div style={{ padding: `14px ${pad} 0`, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
            <button
              onClick={onClose}
              style={{ width: "34px", height: "34px", borderRadius: "999px", border: appTheme.pillBorder, background: appTheme.pillBackground, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M1 1l10 10M11 1L1 11" stroke={appTheme.secondaryText} strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </button>
            <div style={{ fontSize: "13px", fontWeight: "600", color: appTheme.secondaryText, textAlign: "center", flex: 1, margin: "0 12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {recipe?.name}
            </div>
            <div style={{ width: "34px" }} />
          </div>
          {/* Progress bar */}
          <div style={{ height: "5px", borderRadius: "999px", background: appTheme.softBackground, overflow: "hidden" }}>
            <div style={{
              height: "100%", borderRadius: "999px", background: ACCENT,
              width: `${progressPct}%`, transition: "width 0.35s ease",
            }} />
          </div>
          <div style={{ fontSize: "11px", color: appTheme.secondaryText, textAlign: "right", marginTop: "5px", fontWeight: "500" }}>
            Step {currentStep + 1} of {totalSteps}
          </div>
        </div>

        {/* Step content */}
        <div className="modal-scroll-hidden" style={{ flex: 1, overflowY: "auto", padding: `24px ${pad} 0` }}>
          <div style={{ fontSize: isMobile ? "13px" : "14px", fontWeight: "700", color: ACCENT, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "10px" }}>
            Step {currentStep + 1}
          </div>
          <div style={{ fontSize: isMobile ? "22px" : "26px", fontWeight: "600", fontFamily: appTheme.fontHeading, color: appTheme.primaryText, lineHeight: 1.2, marginBottom: "14px" }}>
            {currentStepData.title}
          </div>
          <div style={{ fontSize: isMobile ? "16px" : "18px", color: appTheme.secondaryText, lineHeight: 1.75, marginBottom: "28px", fontFamily: appTheme.fontBody }}>
            {currentStepData.body}
          </div>

          {/* Timer */}
          {currentStepData.time && (
            <div style={{ marginBottom: "24px" }}>
              {timerSeconds === null && !timerDone ? (
                <button
                  onClick={() => handleTimerPress(currentStepData.time)}
                  style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "11px 18px", borderRadius: "999px", border: appTheme.pillBorder, background: appTheme.pillBackground, color: appTheme.primaryText, fontSize: "14px", fontWeight: "600", cursor: "pointer" }}
                >
                  <svg width="16" height="19" viewBox="0 0 18 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 3C7.21997 3 5.47991 3.52784 3.99987 4.51677C2.51983 5.50571 1.36628 6.91131 0.685088 8.55585C0.00389957 10.2004 -0.17433 12.01 0.172937 13.7558C0.520204 15.5016 1.37737 17.1053 2.63604 18.364C3.89472 19.6226 5.49836 20.4798 7.24419 20.8271C8.99002 21.1743 10.7996 20.9961 12.4442 20.3149C14.0887 19.6337 15.4943 18.4802 16.4832 17.0001C17.4722 15.5201 18 13.78 18 12C17.9973 9.61389 17.0482 7.32629 15.361 5.63905C13.6737 3.95182 11.3861 3.00273 9 3ZM9 19.5C7.51664 19.5 6.0666 19.0601 4.83323 18.236C3.59986 17.4119 2.63856 16.2406 2.07091 14.8701C1.50325 13.4997 1.35473 11.9917 1.64411 10.5368C1.9335 9.08197 2.64781 7.74559 3.6967 6.6967C4.7456 5.64781 6.08197 4.9335 7.53683 4.64411C8.99168 4.35472 10.4997 4.50325 11.8701 5.0709C13.2406 5.63856 14.4119 6.59986 15.236 7.83322C16.0601 9.06659 16.5 10.5166 16.5 12C16.4978 13.9884 15.7069 15.8948 14.3008 17.3008C12.8948 18.7069 10.9884 19.4978 9 19.5ZM13.2806 7.71938C13.3504 7.78903 13.4057 7.87175 13.4434 7.9628C13.4812 8.05384 13.5006 8.15144 13.5006 8.25C13.5006 8.34856 13.4812 8.44616 13.4434 8.53721C13.4057 8.62825 13.3504 8.71097 13.2806 8.78063L9.53063 12.5306C9.46095 12.6003 9.37822 12.6556 9.28718 12.6933C9.19613 12.731 9.09855 12.7504 9 12.7504C8.90146 12.7504 8.80388 12.731 8.71283 12.6933C8.62179 12.6556 8.53906 12.6003 8.46938 12.5306C8.3997 12.4609 8.34442 12.3782 8.30671 12.2872C8.269 12.1961 8.24959 12.0985 8.24959 12C8.24959 11.9015 8.269 11.8039 8.30671 11.7128C8.34442 11.6218 8.3997 11.5391 8.46938 11.4694L12.2194 7.71938C12.289 7.64964 12.3718 7.59432 12.4628 7.55658C12.5538 7.51884 12.6514 7.49941 12.75 7.49941C12.8486 7.49941 12.9462 7.51884 13.0372 7.55658C13.1283 7.59432 13.211 7.64964 13.2806 7.71938ZM6 0.75C6 0.551088 6.07902 0.360322 6.21967 0.21967C6.36033 0.0790176 6.55109 0 6.75 0H11.25C11.4489 0 11.6397 0.0790176 11.7803 0.21967C11.921 0.360322 12 0.551088 12 0.75C12 0.948912 11.921 1.13968 11.7803 1.28033C11.6397 1.42098 11.4489 1.5 11.25 1.5H6.75C6.55109 1.5 6.36033 1.42098 6.21967 1.28033C6.07902 1.13968 6 0.948912 6 0.75Z" fill="currentColor"/>
                  </svg>
                  {currentStepData.time} min timer
                </button>
              ) : timerDone ? (
                <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "11px 18px", borderRadius: "999px", border: "1px solid rgba(134,239,172,0.7)", background: "rgba(220,252,231,0.6)", animation: "pulse 1s ease 3" }}>
                  <span style={{ fontSize: "14px", fontWeight: "700", color: "#14532d" }}>Done! ✓</span>
                  <button
                    onClick={() => handleTimerPress(currentStepData.time)}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: "12px", color: "#14532d", fontWeight: "600", padding: 0, opacity: 0.7 }}
                  >
                    Restart
                  </button>
                </div>
              ) : (
                <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                  <button
                    onClick={() => handleTimerPress(currentStepData.time)}
                    style={{ display: "inline-flex", alignItems: "center", gap: "10px", padding: "11px 18px", borderRadius: "999px", border: `1px solid ${ACCENT}44`, background: appTheme.accentLight, color: ACCENT, fontSize: "15px", fontWeight: "700", cursor: "pointer" }}
                  >
                    <span style={{ fontSize: "20px", fontWeight: "800", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>
                      {formatTimer(timerSeconds)}
                    </span>
                    <span style={{ fontSize: "12px", opacity: 0.7 }}>{timerRunning ? "Tap to pause" : "Tap to resume"}</span>
                  </button>
                  <button
                    onClick={resetTimer}
                    title="Reset timer"
                    style={{ width: "40px", height: "40px", borderRadius: "999px", border: appTheme.pillBorder, background: appTheme.pillBackground, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                  >
                    <ArrowCounterClockwise size={16} color={appTheme.secondaryText} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Tip card */}
          <div style={{
            padding: "14px 16px", borderRadius: "16px",
            background: appTheme.cardBackground, border: appTheme.cardBorder,
            display: "flex", gap: "12px", alignItems: "flex-start",
            marginBottom: "32px",
          }}>
            <span style={{ fontSize: "18px", flexShrink: 0, marginTop: "1px" }}>💡</span>
            <span style={{ fontSize: "13px", color: appTheme.secondaryText, lineHeight: 1.6, fontStyle: "italic" }}>
              {currentTip}
            </span>
          </div>
        </div>

        {/* Bottom nav */}
        <div style={{ padding: `8px ${pad} ${isMobile ? "28px" : "20px"}`, borderTop: "1px solid rgba(0,0,0,0.06)", background: appTheme.pageBackground, flexShrink: 0 }}>
          <div style={{ textAlign: "center", marginBottom: "12px" }}>
            <button
              onClick={() => {
                if (currentStep < totalSteps - 1) setCurrentStep((s) => s + 1);
                else setScreen("complete");
              }}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: "12px", fontWeight: "600", color: appTheme.secondaryText, padding: "4px 8px" }}
            >
              Skip step
            </button>
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            {currentStep > 0 && (
              <button
                onClick={() => setCurrentStep((s) => s - 1)}
                style={{ flex: 1, padding: "14px", borderRadius: "14px", border: appTheme.pillBorder, background: appTheme.pillBackground, color: appTheme.primaryText, fontSize: "15px", fontWeight: "600", cursor: "pointer" }}
              >
                ← Previous
              </button>
            )}
            <button
              onClick={() => {
                if (isLastStep) setScreen("complete");
                else setCurrentStep((s) => s + 1);
              }}
              style={{ flex: currentStep > 0 ? 2 : 1, padding: "14px", borderRadius: "14px", border: "none", background: ACCENT, color: "#fff", fontSize: "15px", fontWeight: "700", cursor: "pointer" }}
            >
              {isLastStep ? "Finish 🎉" : "Next →"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Screen 3: Complete ─────────────────────────────────────────────────────
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 2500,
      background: appTheme.pageBackground,
      display: "flex", flexDirection: "column",
      animation: "settingsSheetIn 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
    }}>
      {/* Top bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", padding: `14px ${pad} 0`, flexShrink: 0 }}>
        <button
          onClick={onClose}
          style={{ width: "34px", height: "34px", borderRadius: "999px", border: appTheme.pillBorder, background: appTheme.pillBackground, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M1 1l10 10M11 1L1 11" stroke={appTheme.secondaryText} strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </button>
      </div>

      <div className="modal-scroll-hidden" style={{ flex: 1, overflowY: "auto", padding: `32px ${pad} 0` }}>
        {/* Completion message */}
        <div style={{ marginBottom: "40px" }}>
          <div style={{ fontSize: isMobile ? "28px" : "34px", fontWeight: "600", fontFamily: appTheme.fontHeading, color: appTheme.primaryText, lineHeight: 1.2, marginBottom: "10px" }}>
            {completionMessage.current}
          </div>
          <div style={{ fontSize: "14px", color: appTheme.secondaryText, fontWeight: "500" }}>
            {recipe?.name}
          </div>
        </div>

        {/* Mark as cooked */}
        <button
          onClick={() => {
            if (!isCooked) {
              onMarkCooked?.();
              setIsCooked(true);
            }
          }}
          style={{
            width: "100%", padding: "15px", borderRadius: "16px", marginBottom: "24px",
            border: isCooked ? "1px solid rgba(134,239,172,0.7)" : "none",
            background: isCooked ? "rgba(220,252,231,0.65)" : ACCENT,
            color: isCooked ? "#14532d" : "#fff",
            fontSize: "16px", fontWeight: "700", cursor: isCooked ? "default" : "pointer",
            transition: "all 0.25s ease",
          }}
        >
          {isCooked ? "✓ Marked as cooked" : "Mark as cooked"}
        </button>

        {/* Recipe note */}
        <div style={{ marginBottom: "8px" }}>
          <div style={{ fontSize: "12px", fontWeight: "700", color: appTheme.secondaryText, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "10px" }}>
            Recipe notes
          </div>
          <textarea
            value={noteDraft}
            onChange={(e) => { setNoteDraft(e.target.value); setNoteSaved(false); }}
            placeholder="Anything you'd do differently next time?"
            rows={4}
            style={{
              width: "100%", boxSizing: "border-box",
              padding: "13px 14px", borderRadius: "14px",
              border: appTheme.accentBorder, background: appTheme.inputBackground,
              fontSize: "14px", color: appTheme.primaryText,
              lineHeight: 1.6, resize: "none", outline: "none",
              fontFamily: appTheme.fontBody, marginBottom: "10px",
            }}
          />
          <button
            onClick={() => {
              onSaveNote?.(noteDraft.trim());
              setNoteSaved(true);
            }}
            style={{
              padding: "9px 18px", borderRadius: "999px",
              border: appTheme.accentBorder, background: appTheme.accentLight,
              color: ACCENT, fontSize: "13px", fontWeight: "700", cursor: "pointer",
            }}
          >
            {noteSaved ? "✓ Saved" : "Save note →"}
          </button>
        </div>
      </div>

      {/* Back link */}
      <div style={{ padding: `12px ${pad} ${isMobile ? "32px" : "20px"}`, textAlign: "center", flexShrink: 0 }}>
        <button
          onClick={onClose}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: "13px", fontWeight: "600", color: appTheme.secondaryText }}
        >
          ← Back to recipe
        </button>
      </div>
    </div>
  );
}

export default CookingMode;
